import type { ReactNode } from 'react';
import type { Metric, MetricsBlock } from '../../api/types.js';
import { color, font, fontSize } from '../../theme/tokens.js';
import { formatValueExact } from '../MetricCell/format.js';
import { sourceFace, type MetricProvenance } from './provenance.js';

/**
 * From `./format`, not from `<MetricCell>` — that module imports this one, and a cycle between
 * them would be a runtime hazard for what is, in the end, arithmetic. Both import the shared
 * module, so the compaction threshold and FR-055's `—` cannot differ between a cell and the
 * panel decomposing it.
 *
 * These rows are **exact**. The panel is where a reader goes for the precise figure, so
 * compacting here would defeat the thing that makes compacting the cell safe.
 */

/**
 * The hover breakdown shared by the chart and the Region Performance table (blueprint § tooltip).
 *
 * One number on screen, decomposed along the three filter axes. Two rules make it trustworthy:
 *
 * - **Every row varies exactly ONE axis from what is displayed**, holding the other two where the
 *   user put them. So the rows decompose the figure actually being hovered. Decomposing the
 *   unfiltered total instead would print numbers that do not add up to the point under the
 *   cursor, which is worse than showing nothing.
 * - **`External` is derived, never fetched** — `total − internal`, computed here. Two separately
 *   sourced numbers can drift; a subtraction cannot.
 *
 * ⚠ There is deliberately **no `Prev Emp` row**, though the blueprint shows one. PEMP is a
 * candidate status from `ch_candidate_new`; it is not a property of a placement, a job, or a
 * submittal. The blueprint could show it only because the prototype generated every breakdown
 * figure with `rnd()` — the shape is the blueprint's contribution, the numbers were never real.
 */

export interface BreakdownRow {
  label: string;
  value: string;
  color: string;
}

/** Blueprint row colours. Internal/Nursing share a teal, External/Allied share an indigo. */
const ROW_COLOR = {
  internal: color.nursing,
  external: color.allied,
  target: color.target,
  nursing: color.nursing,
  allied: color.allied,
} as const;

function row(label: string, value: number | null, tone: keyof typeof ROW_COLOR): BreakdownRow {
  return { label, value: formatValueExact(value), color: ROW_COLOR[tone] };
}

/**
 * Rows for one metric, given the displayed value and its three single-axis siblings.
 *
 * `internal`/`target` are omitted for JOBS by its caller rather than here — a job's region comes
 * from its own client, so the Internal test is trivially true for every job and the row would
 * assert a split that does not exist (PRD §9.1.2).
 */
export function breakdownRows(input: {
  total: number | null;
  internal?: number | null;
  target?: number | null;
  nursing: number | null;
  allied: number | null;
  includeSource?: boolean;
  includeTargeting?: boolean;
}): BreakdownRow[] {
  const rows: BreakdownRow[] = [];

  if (input.includeSource !== false) {
    const internal = input.internal ?? null;
    // Only subtract when both sides are real; `null − n` is not zero, it is unknown.
    const external =
      input.total !== null && internal !== null ? Math.max(0, input.total - internal) : null;
    rows.push(row('Internal', internal, 'internal'));
    rows.push(row('External', external, 'external'));
  }
  if (input.includeTargeting !== false)
    rows.push(row('Target', input.target ?? null, 'target'));

  rows.push(row('Nursing', input.nursing, 'nursing'));
  rows.push(row('Allied', input.allied, 'allied'));

  return rows;
}

/** Reads one metric key out of a sibling combo, tolerating a combo the server did not send. */
export function metricValue(block: MetricsBlock | undefined, key: string): number | null {
  const metric = block?.[key] as Metric | null | undefined;
  return metric?.value ?? null;
}

/**
 * The provenance footer — one plain sentence saying how the figure is counted, then the system
 * it came from (owner request 2026-09-10).
 *
 * It sits at the **bottom** of every panel, under its own hairline, and that position is the
 * whole design: the rows above it are the figure, and this is the note about the figure. Put at
 * the top it becomes the first thing read on a tooltip a reader opened to see a number.
 *
 * Three things it is deliberately not:
 *
 * - **Not the filters.** The period, region, Source, Targeting and Specialty controls are on
 *   screen and already say what they narrow. A sentence repeating them is one more thing that
 *   can disagree with the page, in the one place a reader goes when they already do not trust
 *   the number.
 * - **Not the column names.** "Offers divided by submissions" rather than `offersIn / subsIn`.
 *   The reader is a leader asking why a figure is 4%, not someone about to read the transform.
 * - **Not rendered at all where the key is unknown** — `provenanceFor` returns `undefined` and
 *   this returns `null`. A wrong lineage is worse than a missing one.
 *
 * `maxWidth` is what keeps the panel from pinning to its 280px ceiling on every cell in a table:
 * `.disclosure-panel` is `min-width: max-content`, so an unconstrained sentence would make every
 * tooltip in the product as wide as the CSS allows. A child's `max-width` caps its own
 * max-content contribution, which is what settles the bubble at ~242px instead.
 */
export function ProvenanceFootnote({
  provenance,
}: {
  provenance: MetricProvenance | null | undefined;
}) {
  if (!provenance) return null;

  return (
    <span
      style={{
        display: 'block',
        maxWidth: 216,
        marginTop: 6,
        paddingTop: 5,
        borderTop: `1px solid ${color.tipBorderInner}`,
        fontSize: fontSize.micro,
        fontWeight: 500,
        lineHeight: 1.4,
        color: color.tipInkRow,
      }}
    >
      <span style={{ display: 'block' }}>{provenance.formula}</span>
      <span
        style={{
          display: 'block',
          marginTop: 3,
          fontWeight: 700,
          letterSpacing: '.2px',
          color: color.tipInk,
        }}
      >
        {`Source: ${sourceFace(provenance.sources)}`}
      </span>
    </span>
  );
}

export interface BreakdownPanelProps {
  title: string;
  /** The headline the rows decompose. Rendered first — the blueprint's `Value` row, labelled
   *  `Total` (owner request 2026-09-02): the rows under it are the parts of this figure. */
  total?: string;
  totalColor?: string;
  rows: BreakdownRow[];
  /**
   * How this figure is counted and where it comes from — rendered as the panel's last block.
   *
   * Optional, and omitted rather than defaulted: a panel decomposing something with no single
   * metric key behind it (the chart's `TOTAL` cell over twelve buckets, say) has no honest
   * answer, and inventing one is the failure mode `provenanceFor` refuses.
   */
  provenance?: MetricProvenance | null;
  /**
   * The cell's trend section (`<MetricCell>`'s `TrendSection`), rendered between the rows and the
   * provenance footer — a node rather than two numbers, so the panel stays ignorant of what a
   * trend window is and the one definition of how it reads lives with the arrow it explains.
   */
  trend?: ReactNode;
}

/**
 * The bubble body — title, optional total, then label/value rows.
 *
 * Rendered inside whatever positions it: the table hands it to `<Disclosure panel>` so it
 * inherits hover *and* keyboard focus (FR-062).
 *
 * The **chart no longer uses this** (2026-08-29): its bubble decomposes every visible series at
 * once, and four of these panels do not fit a 240px plot, so it lays out its own compact sections
 * from `breakdownRows` above. That function is the shared part and the part that matters — a
 * second definition of "External is total − internal", or of which axes JOBS has, is the drift
 * worth preventing. Two layouts for two very different densities is not.
 */
export function BreakdownPanel({
  title,
  total,
  totalColor,
  rows,
  provenance,
  trend,
}: BreakdownPanelProps) {
  return (
    <span style={{ display: 'block', minWidth: 168 }}>
      <span
        style={{
          display: 'block',
          fontSize: fontSize.caption,
          fontWeight: 700,
          letterSpacing: '.2px',
          color: color.tipInk,
          marginBottom: 5,
          paddingBottom: 5,
          borderBottom: `1px solid ${color.tipBorderInner}`,
        }}
      >
        {title}
      </span>

      {total !== undefined ? (
        <span
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 12,
            fontSize: fontSize.captionStrong,
            padding: '1.5px 0',
          }}
        >
          <span>Total</span>
          <span
            className="mono"
            style={{ fontFamily: font.mono, fontWeight: 700, color: totalColor ?? 'inherit' }}
          >
            {total}
          </span>
        </span>
      ) : null}

      {rows.map((r) => (
        <span
          key={r.label}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 12,
            fontSize: fontSize.captionStrong,
            padding: '1.5px 0',
          }}
        >
          <span>{r.label}</span>
          <span
            className="mono"
            style={{ fontFamily: font.mono, fontWeight: 700, color: r.color }}
          >
            {r.value}
          </span>
        </span>
      ))}

      {trend}
      <ProvenanceFootnote provenance={provenance} />
    </span>
  );
}
